import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
import { IOutletTransactionMandateResponse } from './createTransactionMandate';
import { getOutletTransactionMandatesAsync } from './getOutletTransactionMandates';

interface IUpdateOutletTransactionMandateRequest {
  id: string;
  name: string;
  categoryId: string;
  minimumAmount: number;
  pin: string;
  outletId: string;
  byPassAdminTransactions: boolean;
  isAllTransactions: boolean;
  approvalFlow: 'sequential' | 'flat'; //flat or sequential
  amounts: {
    approvers: {
      teamMemberIds: string[];
    }[];
    maximumAmount?: number;
  }[];
}

export interface ThresholdFormValues {
  id: string;
  thresholdAmount: string;
  approvalBy: string[];
}

const initialState: IApiState<IOutletTransactionMandateResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const updateOutletTransactionMandateAsync = createAsyncThunk(
  'outletTransactionMandate/update',
  async (
    values: IUpdateOutletTransactionMandateRequest,
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.put<IOutletTransactionMandateResponse>(
        `/transaction/outlet-mandates/${values.id}`,
        values,
        config,
      );

      dispatch(getOutletTransactionMandatesAsync());

      dispatch(
        showAlert({
          status: 'success',
          description:
            data?.message ?? 'Transaction Mandate updated successfully',
        }),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IOutletTransactionMandateResponse>;

      dispatch(
        showAlert({
          status: 'error',
          description: error.response?.data?.message ?? 'An error occurred',
        }),
      );

      return rejectWithValue(error.response?.data);
    }
  },
);

export const outletTransactionMandateUpdateSlice = createSlice({
  name: 'outletTransactionMandate/update',
  initialState,
  reducers: {
    resetOutletTransactionMandateUpdate(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateOutletTransactionMandateAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(
        updateOutletTransactionMandateAsync.fulfilled,
        (state, action) => {
          state.status = 'success';
          state.value = action.payload as IOutletTransactionMandateResponse;
        },
      )
      .addCase(
        updateOutletTransactionMandateAsync.rejected,
        (state, action) => {
          state.status = 'failed';
          state.value = action.payload as IOutletTransactionMandateResponse;
          state.error = action.error.message;
        },
      );
  },
});

export const { resetOutletTransactionMandateUpdate } =
  outletTransactionMandateUpdateSlice.actions;
export default outletTransactionMandateUpdateSlice.reducer;
